import React, { useState } from "react";
import {
  Box,
  VStack,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Button,
  Text,
  useToast,
  Link,
  HStack,
  Image,
  Divider,
  FormErrorMessage,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { authAPI } from "../services/api";

const ForgotPassword = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [errors, setErrors] = useState<{ email?: string }>({});

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset errors
    setErrors({});
    
    // Validate email
    if (!email) {
      setErrors({ email: "Email is required" });
      return;
    }
    
    if (!validateEmail(email)) {
      setErrors({ email: "Please enter a valid email address" });
      return;
    }

    setLoading(true);

    try {
      await authAPI.forgotPassword(email);
      setEmailSent(true);
      toast({
        title: "Email Sent",
        description: "Please check your email for password reset instructions.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.response?.data?.message || "Failed to send reset email",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  return (
    <Box minH="100vh" bgGradient="linear(to-br, primary.50, white 60%)" display="flex" alignItems="center" justifyContent="center" p={4}>
      <Box maxW="400px" w="full" bg="white" borderRadius="2xl" boxShadow="2xl" p={8} borderWidth={1} borderColor="primary.100">
        <VStack spacing={6} align="stretch">
          {/* Logo & Title */}
          <VStack spacing={2} textAlign="center">
            <Image src="/favicon.png" alt="CMS Logo" boxSize="48px" mx="auto" />
            <Heading size="lg" color="primary.700">Forgot Password</Heading>
            <Text color="gray.500" fontSize="md">
              {emailSent 
                ? "Check your email for reset instructions" 
                : "Enter your email to receive password reset instructions"
              }
            </Text>
          </VStack>
          <Divider />

          {!emailSent ? (
            <>
              {/* Forgot Password Form */}
              <form onSubmit={handleSubmit}>
                <VStack spacing={4} align="stretch">
                  <FormControl isInvalid={!!errors.email}>
                    <FormLabel>Email Address</FormLabel>
                    <Input
                      type="email"
                      placeholder="Enter your email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      size="lg"
                      bg="primary.50"
                      borderColor="primary.200"
                      _focus={{ borderColor: "primary.400", boxShadow: "0 0 0 2px #7B4BC1" }}
                    />
                    <FormErrorMessage>{errors.email}</FormErrorMessage>
                  </FormControl>

                  <Button
                    type="submit"
                    colorScheme="primary"
                    size="lg"
                    width="full"
                    isLoading={loading}
                    loadingText="Sending..."
                    fontWeight="bold"
                    boxShadow="md"
                  >
                    Send Reset Email
                  </Button>
                </VStack>
              </form>
            </>
          ) : (
            /* Success State */
            <VStack spacing={4} align="stretch">
              <Box
                bg="green.50"
                border="1px"
                borderColor="green.200"
                borderRadius="md"
                p={4}
                textAlign="center"
              >
                <Text color="green.700" fontWeight="medium">
                  Password reset email sent successfully!
                </Text>
                <Text color="green.600" fontSize="sm" mt={2}>
                  Please check your email and follow the instructions to reset your password.
                </Text>
              </Box>

              <Button
                variant="outline"
                colorScheme="primary"
                size="lg"
                width="full"
                onClick={() => setEmailSent(false)}
                fontWeight="bold"
              >
                Send Another Email
              </Button>
            </VStack>
          )}

          {/* Back to Login */}
          <HStack justify="center" pt={4}>
            <Link
              color="primary.500"
              fontWeight="bold"
              onClick={handleBackToLogin}
              display="flex"
              alignItems="center"
              gap={2}
              _hover={{ textDecoration: "none" }}
            >
              <ArrowBackIcon />
              Back to Login
            </Link>
          </HStack>
        </VStack>
      </Box>
    </Box>
  );
};

export default ForgotPassword;
